import { useFlyOutProductsBasedOnSelectedCloud } from '../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import { resolveLabraProductIdForPrivateOfferNavigation } from '../../utils/privateOfferNavigation'
import {
  ModalConfirm,
  ModalSizeVariants,
  PlanType,
  TableComponentV2,
} from '@labrav/react-components'
import React, { useCallback, useMemo } from 'react'
import { isEmpty } from 'lodash'
import { useNavigate, useParams } from 'react-router-dom'
import {
  LoadingTypes,
  PartnerType,
  isLoading,
  searchDropDownOptionMapping,
  useAccountId,
  useCurrentCloudProvider,
  useScreenSize,
  useUserType,
} from '@labrav/common'

import { getPrivateOffers } from '../../modules/privateOffer/action'
import {
  PrivateOfferObject,
  PrivateOffersState,
} from '../../modules/privateOffer/reducer'
import { useDispatch, useSelector } from '../../store'
import { CloudMarketplace, Product } from '../../modules/productsListing/types'
import {
  getKeyCloudAccount,
  getPrivateOfferRedirectUrl,
  getProductIdFromPrivateOffer,
} from '../../utils/privateOffersListHelpers'
import type {
  DetailedOfferPreviewComponent,
  DetailedOfferPreviewData,
} from '../PrivateOffers/types'

export const mappingOnAdminUI = {
  'Offer name': 'privateOfferName',
  'Company name': 'companyName',
  'Product name': 'productName',
  'Offer object ID in CRM': 'crmOrderObjectId',
  'Craeted at': 'createdAt',
  'AWS offer ID': 'awsOfferId',
  'AWS offer URL': 'cloudOfferUrl',
  ACTIONS: 'actions',
}

export const getMappingPOBasedOnCloud = (
  cloud: CloudMarketplace
): Record<string, string> => {
  const mappingValue = getKeyCloudAccount(cloud)

  if (cloud === 'AZURE') {
    return {
      Buyer: 'companyName',
      Product: 'productName',
      Offer: 'privateOfferName',
      'Expiry date': 'offerExpirationDate',
      Status: 'offerStatus',
      '': 'actions',
    }
  }
  return {
    Company: 'companyName',
    'Product title': 'productName',
    'Expiry date': 'offerExpirationDate',
    'Created date': 'createdAt',
    Status: 'offerStatus',
    ...(cloud === 'AWS' ? { Type: 'type' } : {}),
    'Offer ID': mappingValue,
    '': 'actions',
  }
}

export type PrivateOffersV2Props = {
  inputValue: string
  selectedOption: string
  privateOffersForTable: Record<string, string | JSX.Element | null>[]
  openModal: boolean
  selectedPrivateOffer: DetailedOfferPreviewData
  handleModalClose: () => void
  crmOpportunityId?: string
  expiringInDateRange?: string
  privateOfferStatus?: string[]
  planType?: PlanType
  selectedCloudTypeProductListing: CloudMarketplace
  externalBaseURL?: string | undefined
  partnerId: string
  products?: Product[]
  tableEl?: React.RefObject<HTMLDivElement>
  appliedFilteredValues?: string[]
  searchApplied?: boolean
  DetailedOfferPreviewComponent?: DetailedOfferPreviewComponent
  trackRowAction?: (params: {
    actionKey: string
    privateOfferId?: string
    cloudType?: string
    offerStatus?: string
  }) => void
}

const PrivateOffersV2 = React.memo(
  ({
    inputValue,
    selectedOption,
    privateOffersForTable,
    crmOpportunityId,
    expiringInDateRange,
    privateOfferStatus,
    openModal,
    selectedPrivateOffer,
    handleModalClose,
    planType,
    selectedCloudTypeProductListing,
    externalBaseURL,
    partnerId,
    products,
    tableEl,
    appliedFilteredValues,
    searchApplied,
    DetailedOfferPreviewComponent,
    trackRowAction,
  }: PrivateOffersV2Props) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { cloud: routeCloud, accountId } = useParams<{
      cloud?: string
      accountId?: string
    }>()
    const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
    const routeAccountId = accountId || fallbackAccountId

    const loading = useSelector<boolean>(state =>
      isLoading(state.loading)(LoadingTypes.GENERAL)
    )

    const { pageNumber, totalCount } = useSelector<PrivateOffersState>(
      state => state.privateOffers
    )

    const { screenWidth } = useScreenSize()
    const columnMaxWidth = useMemo(
      () => `${(screenWidth * 16) / 100}px`,
      [screenWidth]
    )
    const columnMaxWidthOfferId = useMemo(
      () => `${(screenWidth * 5) / 100}px`,
      [screenWidth]
    )
    const columnMaxWidthCompanyName = useMemo(
      () => `${(screenWidth * 12) / 100}px`,
      [screenWidth]
    )

    const privateOffers = useSelector<PrivateOfferObject>(
      state => state.privateOffers.privateOffers
    )
    const { selectedProduct: productId } =
      useFlyOutProductsBasedOnSelectedCloud()

    const cloudMarketplace = useCurrentCloudProvider()
    const isAdmin = useUserType() === PartnerType.Admin

    const { pageSize: pageSizeValue } = useScreenSize()
    const fetchMoreData = useCallback(() => {
      if (productId !== undefined && !isEmpty(products)) {
        if (!isEmpty(inputValue) && !isEmpty(selectedOption)) {
          dispatch(
            getPrivateOffers({
              partnerId,
              cloudMarketplace: selectedCloudTypeProductListing,
              pageSize: pageSizeValue,
              pageNumber: pageNumber + 1,
              productId,
              crmOpportunityId,
              input: inputValue,
              selectedInput:
                (
                  searchDropDownOptionMapping.privateOffersOptionMapping as Record<
                    string,
                    string
                  >
                )[selectedOption] || '',
              sectionName: 'offers',
            })
          )
        } else {
          dispatch(
            getPrivateOffers({
              partnerId,
              cloudMarketplace: selectedCloudTypeProductListing,
              pageSize: pageSizeValue,
              pageNumber: pageNumber + 1,
              productId,
              crmOpportunityId,
              expiringInDays: expiringInDateRange,
              offerStatus: privateOfferStatus,
            })
          )
        }
      }
    }, [
      partnerId,
      pageNumber,
      productId,
      crmOpportunityId,
      expiringInDateRange,
      privateOfferStatus,
      products,
      inputValue,
      selectedOption,
      selectedCloudTypeProductListing,
      pageSizeValue,
      dispatch,
    ])

    const handleClickRow = (selectedRowId: string) => {
      const selectedItem = privateOffers[selectedRowId]
      const labraProductId = resolveLabraProductIdForPrivateOfferNavigation({
        products: products || [],
        selectedProductId: productId,
        offerProductRef: getProductIdFromPrivateOffer(
          selectedItem,
          selectedCloudTypeProductListing
        ),
      })
      if (selectedItem && labraProductId) {
        trackRowAction?.({
          actionKey: 'View-offer-row',
          privateOfferId: selectedItem.privateOfferId,
          cloudType: selectedCloudTypeProductListing,
          offerStatus: selectedItem.offerStatus,
        })
        const redirectURL = getPrivateOfferRedirectUrl({
          cloudType: selectedCloudTypeProductListing,
          offer: selectedItem,
          externalBaseURL: externalBaseURL,
          productId: labraProductId,
          cloud: routeCloud?.toLowerCase(),
          accountId: routeAccountId,
        })
        navigate(redirectURL)
      }
    }

    return (
      <div data-testid="private-offers-table">
        <TableComponentV2
          keyName={'migrationDetails.products'}
          objects={privateOffersForTable}
          mapping={
            isAdmin
              ? mappingOnAdminUI
              : getMappingPOBasedOnCloud(cloudMarketplace)
          }
          count={totalCount || 0}
          fetchMoreData={fetchMoreData}
          selectedItem={[]}
          onChangeCheckbox={() => void 0}
          showCheckbox={false}
          isLoading={loading}
          breakWorkHeader={false}
          outerRef={tableEl}
          onChangeRow={handleClickRow}
          customColumns={['actions']}
          stickyColumns={['actions']}
          customCellProps={[
            { style: { width: columnMaxWidthCompanyName } },
            { style: { width: columnMaxWidth } },
            {
              ...(selectedCloudTypeProductListing === 'AZURE'
                ? { style: { width: columnMaxWidthOfferId } }
                : {}),
            },
            {},
            {},
            { style: { width: columnMaxWidthOfferId } },
          ]}
          tableHeight={
            externalBaseURL
              ? 'calc(100vh - 255px)'
              : appliedFilteredValues?.length
                ? 'calc(100vh - 318px)'
                : `calc(100vh - ${searchApplied ? 280 : 250}px)`
          }
        />
        <ModalConfirm
          title={'Preview'}
          subTitle={
            DetailedOfferPreviewComponent ? (
              <DetailedOfferPreviewComponent
                offer={selectedPrivateOffer}
                planType={planType}
              />
            ) : undefined
          }
          content={''}
          open={openModal}
          titleAcceptButton={'Okay'}
          onAccept={handleModalClose}
          onDecline={handleModalClose}
          variant={ModalSizeVariants.medium}
          hideCancelButton={true}
        />
      </div>
    )
  }
)

export default PrivateOffersV2
